import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

import HeaderLogo from '../HeaderLogo';
import HeaderDrawer from '../HeaderDrawer';
import HeaderUserbox from '../HeaderUserbox';

import HeaderCurrencyConverter from '../HeaderCurrencyConverter/HeaderCurrencyConverter';
import HeaderThemeToggle from '../HeaderThemeToggle/HeaderThemeToggle';
import HeaderDisplaySize from '../HeaderDisplaySize/HeaderDisplaySize';
import { useGetOnMountForSelectedClient } from '../../../hooks';
import { Context as ServiceTypesContext } from '../../../api/serviceTypes/context';
// replace import with actual logo + current url:
import ProjectLogo from '../../../assets/taytan-icon-header.svg';

import NotificationBell from '../../NotificationBell/NotificationBell';
import HeaderIndicators from '../../HeaderIndicators';
import MessagePopup from '../../MessagePopup/MessagePopup';
import MessagePopupSettings from '../../MessagePopup/MessagePopupSettings';
import { ReportIssueHeaderButton } from '../../ReportIssue';
import HelpButton from '../HelpButton/HelpButton';
import headerStyles from './headerStyles';
import { HEADER_HEIGHT } from '../layoutConstants';
import SidebarToggleButton from './SidebarToggleButton';
type Props = { setSidebarToggle: () => void; sidebarToggle: boolean };

function Header({ setSidebarToggle, sidebarToggle }: Props) {
    const classes = headerStyles();
    useGetOnMountForSelectedClient(ServiceTypesContext);
    const theme = useTheme();
    const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <AppBar
            color="secondary"
            className={classes.appHeader}
            position="fixed"
            elevation={11}
            sx={{
                // Pegasus navy (palette.brandHeader); the secondary colour stays
                // for the buttons and icons that use it.
                backgroundColor: (t) => (t.palette as any).brandHeader ?? t.palette.secondary.main,
                // MUI lightens elevated Paper in dark mode with a white overlay;
                // on the header colour that turned navy into a pale blue.
                backgroundImage: 'none',
                // Dark mode: the bar is the sidebar's colour, so a hairline — not
                // a shadow, invisible on dark — is what separates it from the page.
                borderBottom: (t) => (t.palette.mode === 'dark' ? '1px solid var(--separator, #2a2a3d)' : 'none'),
                height: `${HEADER_HEIGHT}px`,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                overflow: 'hidden'
            }}
        >
            <HeaderLogo sidebarToggle={sidebarToggle} />
            {!isLgUp && (
                <Grid container alignItems="center" title="Taytan Dashboard" className={classes.widthUnset}>
                    <Link to="/" className={classes.appLogoLink}>
                        <IconButton color="primary" size="medium" className={classes.appLogoBtn}>
                            <img
                                className={classes.appLogoImg}
                                loading="lazy"
                                alt="Taytan Dashboard"
                                src={ProjectLogo}
                            />
                        </IconButton>
                        {isMdUp && (
                            <Typography variant="subtitle1" className={classes.uppercase}>
                                Taytan
                            </Typography>
                        )}
                    </Link>
                </Grid>
            )}
            <Grid
                container
                wrap="nowrap"
                justifyContent="space-between"
                alignItems="center"
                className={classes.appHeaderToolbar}
                sx={{ flex: 1, pl: '0.4rem', pr: '0.4rem', minWidth: 0 }}
            >
                {isMdUp && (
                    <Grid container alignItems="center" wrap="nowrap" className={classes.widthUnset}>
                        <Box
                            className={clsx(classes.btnToggleCollapse, {
                                [classes.btnToggleCollapseClosed]: sidebarToggle
                            })}
                        >
                            <Tooltip title="Toggle Sidebar" placement="right">
                                {/* The glyph states what the click will do:
                                    open while the rail is closed, and the
                                    reverse. */}
                                <SidebarToggleButton
                                    open={!sidebarToggle}
                                    onToggle={setSidebarToggle}
                                />
                            </Tooltip>
                        </Box>
                    </Grid>
                )}
                <Grid
                    container
                    alignItems="center"
                    wrap="nowrap"
                    className={classes.widthUnset}
                    sx={{ width: 'auto', flexShrink: 0, gap: '4px' }}
                >
                    <HeaderDisplaySize />
                    <HeaderThemeToggle />
                    <HeaderCurrencyConverter />
                    <HeaderIndicators />
                    <NotificationBell />
                    <MessagePopupSettings />
                    <MessagePopup />
                    {/* <HeaderDots /> */}
                    {/* Conventional help/feedback slot: right corner, immediately before the avatar. */}
                    <HelpButton />
                    <ReportIssueHeaderButton />
                    <HeaderUserbox />
                    <HeaderDrawer />
                    {!isLgUp && (
                        <Tooltip title="Toggle Sidebar" placement="right">
                            <SidebarToggleButton open={sidebarToggle} onToggle={setSidebarToggle} />
                        </Tooltip>
                    )}
                </Grid>
            </Grid>
        </AppBar>
    );
}

export default React.memo(Header);
